import { z } from 'zod';

const seatCountSchema = z.preprocess(
    (v) => {
        if (v === '' || v === null || v === undefined) return undefined;
        if (typeof v === 'number' && Number.isNaN(v)) return undefined;
        return v;
    },
    z.coerce.number().int().min(0).optional()
);

const costSchema = z.preprocess(
    (v) => {
        if (v === '' || v === null || v === undefined) return undefined;
        if (typeof v === 'number' && Number.isNaN(v)) return undefined;
        const n = typeof v === 'string' ? Number(v) : v;
        if (typeof n === 'number' && Number.isNaN(n)) return undefined;
        return n;
    },
    z.number({
        required_error: 'assets:validation.required.cost',
        invalid_type_error: 'assets:validation.invalidNumber',
    }).min(0, 'assets:validation.required.cost').optional()
);

function addLicenseDateRefinement(schema) {
    return schema.superRefine((data, ctx) => {
        const start = data.startDate;
        const end = data.endDate;
        if (start === undefined || start === null || end === undefined || end === null) {
            return;
        }
        const s = start instanceof Date ? start : new Date(start);
        const e = end instanceof Date ? end : new Date(end);
        if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e < s) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'assets:form.validation.endDateBeforeStart',
                path: ['endDate'],
            });
        }
    });
}

/** Raw object shape (ZodObject) — apply `.partial()` here before wrapping with `superRefine`. */
const AssetLicenseObjectSchema = z
    .object({
        name: z.string().min(1, 'assets:validation.required.name'),
        vendor: z.string().min(1, 'assets:validation.required.vendor'),
        licenseType: z.string().min(1, 'assets:validation.required.licenseType'),
        licenseKey: z.string().optional(),
        skuOrPartNo: z.string().optional(),
        poReference: z.string().optional(),
        seatCount: seatCountSchema,
        startDate: z.union([z.string(), z.coerce.date()]).optional(),
        endDate: z.union([z.string(), z.coerce.date()]).optional(),
        status: z.enum(['active', 'inactive', 'archived']).optional(),
        linkedAssets: z.array(z.string()).optional(),
        notes: z.string().optional(),
        cost: costSchema,
        currency: z.preprocess(
            (v) => (v === '' || v == null ? undefined : String(v).trim().toUpperCase()),
            z.string().min(1, 'assets:validation.required.currency').max(10).optional(),
        ),
        renewalType: z
            .enum(['none', 'annual', 'monthly', 'multi_year', 'perpetual', 'other'])
            .optional(),
        billingCycle: z.string().max(120).optional(),
        autoRenew: z.boolean().optional(),
        ownerUnit: z
            .preprocess((v) => (v === '' || v == null ? undefined : v), z.string().min(1).max(24).optional()),
        linkedDocuments: z.array(z.string().min(1)).max(50).optional(),
        customFields: z.record(z.unknown()).optional(),
    })
    .passthrough();

export const CreateAssetLicenseSchema = addLicenseDateRefinement(AssetLicenseObjectSchema);

const updateSeatCountSchema = z.preprocess(
    (v) => {
        if (v === '') return null;
        if (v === null || v === undefined) return undefined;
        if (typeof v === 'number' && Number.isNaN(v)) return null;
        return v;
    },
    z.union([z.coerce.number().int().min(0), z.null()]).optional(),
);

/** Update: empty / missing → null; accepts string ObjectId or explicit null (not undefined after parse). */
const updateOwnerUnitSchema = z.preprocess(
    (v) => {
        if (v === '' || v === undefined || v === null) return null;
        return v;
    },
    z.string().min(1).max(24).nullable(),
);

export const UpdateAssetLicenseSchema = addLicenseDateRefinement(
    AssetLicenseObjectSchema.omit({ seatCount: true, ownerUnit: true, cost: true }).partial().extend({
        seatCount: updateSeatCountSchema,
        ownerUnit: updateOwnerUnitSchema.optional(),
        cost: z
            .preprocess(
                (v) => {
                    if (v === null) return null;
                    if (v === '' || v === undefined) return undefined;
                    if (typeof v === 'number' && Number.isNaN(v)) return undefined;
                    const n = typeof v === 'string' ? Number(v) : v;
                    if (typeof n === 'number' && Number.isNaN(n)) return undefined;
                    return n;
                },
                z.union([
                    z.null(),
                    z.number({
                        invalid_type_error: 'assets:validation.invalidNumber',
                    }).min(0, 'assets:validation.required.cost'),
                ]),
            )
            .optional(),
    })
);

export const BatchUpdateAssetLicenseSchema = z.object({
    ids: z.array(z.string().min(1)).min(1),
    patch: z
        .object({
            status: z.enum(['active', 'inactive', 'archived']).optional(),
            linkedAssets: z.array(z.string()).optional(),
        })
        .superRefine((p, ctx) => {
            if (p.status === undefined && p.linkedAssets === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'assets:form.validation.batchAtLeastOneField',
                    path: ['patch'],
                });
            }
        }),
});
